import * as Dialog from "@radix-ui/react-dialog";
import { filterClasses } from "@swivl-mono/helpers";
import { TicketPriority, orderedTicketPriorities, ticketPriorityLabels } from "@swivl-mono/types";
import { Button, ButtonVariant, Select, SelectItem } from "@swivl-mono/ui";
import React, { useEffect, useRef, useState } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { RiDeleteBinLine } from "react-icons/ri";

import { CustomerPickerField, SelectedCustomer } from "../../../Components/CustomerPickerModal";
import { TagInput, TagInputType } from "../../../Components/TagInput/TagInput";
import type { CreatedTicketResult } from "../../../Models/Ticketing/Ticketing.Store";
import { ASSIGNEE_SCOPE_TOOLTIP, LOCATION_SCOPE_TOOLTIP } from "./assigneeLocationScope";
import { FieldLabelWithInfo } from "./fields/FieldLabelWithInfo";
import { type CreateTicketSeed, useCreateTicket } from "./useCreateTicket";

interface CreateTicketModalProps {
  onClose: () => void;
  /** Called after a successful create so the inbox can jump to where the ticket landed. */
  onCreated?: (ticket: CreatedTicketResult) => void;
  /** Initial form values (CC right column's LLM prefill, ENG-2957 §9.4) — applied on mount. */
  seed?: CreateTicketSeed;
}

const priorityOptions: SelectItem[] = [
  { value: TicketPriority.NONE, label: ticketPriorityLabels[TicketPriority.NONE] },
  { type: "divider" },
  ...orderedTicketPriorities.filter((p) => p !== TicketPriority.NONE).map((p) => ({ value: p, label: ticketPriorityLabels[p] }))
];

export function CreateTicketModal({ onClose, onCreated, seed }: CreateTicketModalProps) {
  const {
    selectedTemplateId,
    title,
    setTitle,
    description,
    setDescription,
    assigneeMemberIds,
    setAssigneeMemberIds,
    locationId,
    setLocationId,
    priority,
    setPriority,
    selectedCustomer,
    setSelectedCustomer,
    tags,
    setTags,
    routingGroupSlug,
    setRoutingGroupSlug,
    tasks,
    setTasks,
    newTaskText,
    setNewTaskText,
    showTaskInput,
    setShowTaskInput,
    submitting,
    error,
    templates,
    templatesLoading,
    selectedTemplate,
    assigneeOptions,
    locationOptions,
    groupOptions,
    handleTemplateChange,
    handleSubmit
  } = useCreateTicket(onClose, onCreated, seed);

  // Inline task editing
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingTaskText, setEditingTaskText] = useState("");

  const handleStartEditTask = (task: { id: string; description: string }): void => {
    setEditingTaskId(task.id);
    setEditingTaskText(task.description);
  };

  const handleCancelEditTask = (): void => {
    setEditingTaskId(null);
    setEditingTaskText("");
  };

  const handleSaveEditTask = (): void => {
    if (!editingTaskId) return;
    const trimmed = editingTaskText.trim();
    if (!trimmed) {
      handleCancelEditTask();
      return;
    }
    setTasks(tasks.map((t) => (t.id === editingTaskId ? { ...t, description: trimmed } : t)));
    handleCancelEditTask();
  };

  // Desktop-only: template dropdown
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);

  // Desktop-only: scroll shadows
  const [headerShadow, setHeaderShadow] = useState(false);
  const [footerShadow, setFooterShadow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (templateRef.current && !templateRef.current.contains(event.target as Node)) {
        setTemplateDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateScrollShadows = () => {
    const el = contentRef.current;
    if (!el) return;
    setHeaderShadow(el.scrollTop > 0);
    setFooterShadow(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
  };

  useEffect(() => {
    updateScrollShadows();
  }, [tasks, showTaskInput, error, templates]);

  const handleDesktopTemplateChange = (templateId: number | null) => {
    handleTemplateChange(templateId);
    setTemplateDropdownOpen(false);
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-500 bg-black/20 backdrop-blur-xs" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 z-500 flex h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg bg-white shadow-xl"
          onEscapeKeyDown={(e) => {
            if (showTaskInput) {
              e.preventDefault();
              setShowTaskInput(false);
              setNewTaskText("");
            }
          }}
        >
          {/* Header */}
          <div className={filterClasses("relative z-10 flex shrink-0 items-center justify-between px-4 py-4", headerShadow && "shadow-panel")}>
            <Dialog.Title className="text-base font-semibold text-black">Create Ticket</Dialog.Title>
            <Dialog.Close className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-grey-600 hover:bg-grey-50 hover:text-black">
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 4l8 8M4 12l8-8" strokeLinecap="round" />
              </svg>
            </Dialog.Close>
          </div>

          {/* Content */}
          <div ref={contentRef} onScroll={updateScrollShadows} className="flex-1 overflow-auto px-4 pb-4">
            {error && <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-600">{error}</div>}

            {/* Template Button */}
            {templates.length > 0 && (
              <div className="mb-4" ref={templateRef}>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                    disabled={templatesLoading}
                    className="flex h-8 items-center gap-2 rounded border border-grey-200 bg-white px-3 text-sm text-black hover:bg-grey-50"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
                      <rect x="2" y="2" width="12" height="12" rx="2" />
                      <path d="M5 5h6M5 8h6M5 11h3" strokeLinecap="round" />
                    </svg>
                    {selectedTemplate?.name ?? "Template"}
                  </button>

                  {templateDropdownOpen && (
                    <div className="absolute top-full left-0 z-20 mt-1 max-h-48 w-56 overflow-auto rounded border border-grey-200 bg-white py-1 shadow-lg">
                      <button
                        type="button"
                        onClick={() => handleDesktopTemplateChange(null)}
                        className={filterClasses(
                          "w-full px-3 py-2 text-left text-sm hover:bg-grey-50",
                          !selectedTemplateId && "bg-grey-50 font-semibold"
                        )}
                      >
                        No Template
                      </button>
                      {templates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => handleDesktopTemplateChange(template.id)}
                          className={filterClasses(
                            "w-full px-3 py-2 text-left text-sm hover:bg-grey-50",
                            selectedTemplateId === template.id && "bg-grey-50 font-semibold"
                          )}
                        >
                          {template.name}
                          {template.isDefault && <span className="ml-1 text-grey-600">(Default)</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Title */}
            <div className="mb-4">
              <TagInput
                label="Title"
                required
                value={title}
                onSet={setTitle}
                placeholder="Input"
                type={TagInputType.SingleLine}
                autoFocus
                containerClassName="w-full"
              />
            </div>

            {/* Description */}
            <div className="mb-4">
              <TagInput label="Description" value={description} onSet={setDescription} placeholder="Input" />
            </div>

            {/* Location — above Assignee because the assignee options are
                scoped to it (ENG-2961). */}
            <div className="mb-4">
              <Select
                label={<FieldLabelWithInfo label="Location" tooltip={LOCATION_SCOPE_TOOLTIP} />}
                isSearchable
                openOnFocus={false}
                hideClear
                placeholder="Select or Search"
                options={locationOptions}
                selectedValues={locationId ? [String(locationId)] : [""]}
                onChange={(values) => setLocationId(values[0] && values[0] !== "" ? Number(values[0]) : null)}
              />
            </div>

            {/* Assignee */}
            <div className="mb-4">
              <Select
                label={<FieldLabelWithInfo label="Assignee" tooltip={ASSIGNEE_SCOPE_TOOLTIP} />}
                isSearchable
                openOnFocus={false}
                placeholder="Select or Search"
                options={assigneeOptions}
                selectedValues={assigneeMemberIds.map(String)}
                onChange={(values) => setAssigneeMemberIds(values.map(Number))}
              />
            </div>

            {/* Group — dispatches the new ticket to this group's inbox, where
                anyone in the group can accept it. */}
            <div className="mb-4">
              <Select
                label="Group"
                isSearchable
                openOnFocus={false}
                hideClear
                placeholder="Select"
                options={groupOptions}
                selectedValues={[routingGroupSlug ?? ""]}
                onChange={(values) => setRoutingGroupSlug(values[0] && values[0] !== "" ? values[0] : null)}
              />
              {routingGroupSlug && assigneeMemberIds.length > 0 && (
                <p className="body-sm mt-0.5 text-grey-600">
                  Explicit assignees override group routing. This ticket will go to the assignee, not the group inbox.
                </p>
              )}
            </div>

            {/* Customer */}
            <div className="mb-4">
              <label className="mb-1.5 block text-sm font-semibold text-grey-600">Customer</label>
              <CustomerPickerField
                selectedCustomer={selectedCustomer}
                onSelect={(customer: SelectedCustomer) => {
                  setSelectedCustomer(customer);
                }}
                onRemove={() => {
                  setSelectedCustomer(null);
                }}
              >
                {({ onClick }) => (
                  <button
                    type="button"
                    onClick={onClick}
                    className="flex h-[34px] w-full cursor-pointer items-center rounded-sm border border-grey-400 bg-linear-to-b from-white to-grey-50 pr-3 text-sm transition-all duration-200 ease-in-out hover:border-orange-500"
                  >
                    <span className={filterClasses("ml-3 flex-1 truncate text-left", selectedCustomer?.name ? "text-black" : "text-grey-500")}>
                      {selectedCustomer?.name ?? "Select"}
                    </span>
                    <IoIosArrowDown className="ml-auto size-4" />
                  </button>
                )}
              </CustomerPickerField>
            </div>

            {/* Priority */}
            <div className="mb-4">
              <Select
                label="Priority"
                hideClear
                placeholder="Select"
                options={priorityOptions}
                selectedValues={[priority]}
                onChange={(values) => setPriority((values[0] as TicketPriority) ?? TicketPriority.NONE)}
              />
            </div>

            {/* Tags */}
            <div className="mb-4">
              <TagInput
                label="Tags"
                value={tags}
                onSet={setTags}
                placeholder="Comma-separated tags, e.g., urgent, frontend"
                type={TagInputType.SingleLine}
                containerClassName="w-full"
              />
            </div>

            {/* Tasks */}
            <div className="mb-4">
              <label className="mb-1.5 block text-sm text-grey-600">Tasks</label>

              {/* Task list & input wrapper */}
              {(tasks.length > 0 || showTaskInput) && (
                <div className="mb-2 space-y-3 rounded border border-grey-200 bg-grey-50 p-4">
                  {tasks.map((task) => {
                    const isEditing = editingTaskId === task.id;
                    return (
                      <div
                        key={task.id}
                        className={filterClasses(
                          "flex min-h-[42px] items-center gap-2 rounded border bg-white px-3",
                          isEditing ? "border-orange-500" : "border-grey-400"
                        )}
                        onKeyDown={(e) => {
                          if (isEditing && e.key === "Escape") handleCancelEditTask();
                        }}
                      >
                        <div className="h-4 w-4 shrink-0 rounded border border-grey-400 bg-white" />
                        {isEditing ? (
                          <TagInput
                            value={editingTaskText}
                            onSet={setEditingTaskText}
                            onBlur={handleSaveEditTask}
                            onReturnPressed={handleSaveEditTask}
                            type={TagInputType.NoBorder}
                            autoFocus
                            containerClassName="flex-1 min-w-0"
                            className="text-sm text-black"
                          />
                        ) : (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() => handleStartEditTask(task)}
                            onKeyDown={(e) => e.key === "Enter" && handleStartEditTask(task)}
                            className="flex-1 cursor-text text-sm text-black"
                          >
                            {task.description}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setTasks(tasks.filter((t) => t.id !== task.id))}
                          className="shrink-0 cursor-pointer text-grey-900 hover:text-orange-500"
                        >
                          <RiDeleteBinLine className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}

                  {showTaskInput && (
                    <div className="flex min-h-[42px] items-center gap-2 rounded border border-orange-500 bg-white px-3">
                      <div className="h-4 w-4 shrink-0 rounded border border-grey-400 bg-white" />
                      <TagInput
                        value={newTaskText}
                        onSet={setNewTaskText}
                        onReturnPressed={() => {
                          if (newTaskText.trim()) {
                            setTasks([...tasks, { id: `task-${Date.now()}`, description: newTaskText.trim() }]);
                          } else {
                            setShowTaskInput(false);
                          }
                          setNewTaskText("");
                        }}
                        onBlur={() => {
                          if (newTaskText.trim()) {
                            setTasks([...tasks, { id: `task-${Date.now()}`, description: newTaskText.trim() }]);
                          }
                          setNewTaskText("");
                          setShowTaskInput(false);
                        }}
                        placeholder="New Item"
                        type={TagInputType.NoBorder}
                        autoFocus
                        containerClassName="flex-1 min-w-0"
                        className="text-sm text-black"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowTaskInput(false);
                          setNewTaskText("");
                        }}
                        className="shrink-0 cursor-pointer text-grey-900 hover:text-orange-500"
                      >
                        <RiDeleteBinLine className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
              <Button
                variant={ButtonVariant.GHOST}
                className="rounded-sm border border-grey-200 hover:border-orange-500"
                onClick={() => setShowTaskInput(true)}
              >
                Add New Item
              </Button>
            </div>
          </div>

          {/* Footer */}
          <div className={filterClasses("relative z-10 flex shrink-0 justify-end gap-3 px-4 py-4", footerShadow && "shadow-panel-top")}>
            <button
              onClick={onClose}
              disabled={submitting}
              className="h-[26px] cursor-pointer rounded px-3 text-sm font-semibold text-black hover:bg-grey-50 disabled:cursor-not-allowed disabled:opacity-50"
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!title.trim() || submitting}
              className="h-[26px] cursor-pointer rounded bg-orange-500 px-3 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
              type="button"
            >
              {submitting ? "Creating..." : "Confirm"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
